import type { SportDetails } from '../api'
import { useEffect, useRef, useState } from 'react'
import { fetchSportDetails } from '../api'
import DistributionWidget from './DistributionWidget'

// Public, auth-free standalone page for the Distribution carrier grid.
// Mounted at /embed/distribution?sport=<slug>. Designed for cross-origin
// iframe embeds — no portal chrome, no header, no theme toggle, just the
// widget on a transparent background so the host page's color comes
// through.
//
// Auto-resize protocol: the embedded page posts its measured height to
// the parent on every content change. Recipe for partners:
//
//   window.addEventListener('message', (e) => {
//     if (e.data?.type === 'altsportsdata-distribution' && e.data.kind === 'resize') {
//       document.getElementById('my-iframe').style.height = e.data.height + 'px'
//     }
//   })

function getSlugFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search)
  return params.get('sport')
}

export default function DistributionEmbedPage() {
  const slug = getSlugFromUrl()
  const [sport, setSport] = useState<SportDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  // Strip the portal's cream paper background so the iframe goes
  // transparent — partner host page's background shows through.
  useEffect(() => {
    document.documentElement.classList.add('is-embed')
    document.body.classList.add('is-embed')
    return () => {
      document.documentElement.classList.remove('is-embed')
      document.body.classList.remove('is-embed')
    }
  }, [])

  useEffect(() => {
    if (!slug) {
      setError('missing ?sport=<slug>')
      setLoading(false)
      return
    }
    let cancelled = false
    fetchSportDetails(slug)
      .then((d) => {
        if (cancelled) return
        if (!d) setError(`no sport found for "${slug}"`)
        else setSport(d)
      })
      .catch((e) => {
        if (cancelled) return
        setError((e as Error).message)
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
      })
    return () => { cancelled = true }
  }, [slug])

  // postMessage the content height to the parent on every resize. Uses
  // ResizeObserver so flex/wrap reflows (e.g. carrier count changes,
  // narrow viewports) trigger fresh measurements automatically.
  useEffect(() => {
    if (!containerRef.current || typeof window === 'undefined') return
    const send = (h: number) => {
      window.parent?.postMessage(
        { type: 'altsportsdata-distribution', kind: 'resize', height: h },
        '*',
      )
    }
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        send(Math.ceil(entry.contentRect.height))
      }
    })
    ro.observe(containerRef.current)
    return () => ro.disconnect()
  }, [sport])

  return (
    <div ref={containerRef} className="ew-embed-root">
      {error
        ? <p className="b-mono-dim ad-list-empty">⚠ {error}</p>
        : <DistributionWidget sport={sport} loading={loading} />}
    </div>
  )
}
